import React from 'react';
import {
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Add, ChevronLeft, History, Layers, People } from '@material-ui/icons';
import { RouteComponentProps, withRouter } from 'react-router';
import { unpackToDateTime } from '@campfire/hot-date';
import { ConsoleActionButton } from '../../common/ConsoleActionButton';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { GET_ENTERPRISE_SUB_ACCOUNTS } from './enterprise-sub-accounts.gql';
import { GET_SUB_ACCOUNT_ENTITIES } from './enterprise-sub-account-entities.gql';
import { InviteSubAccountAdminDialog } from './InviteSubAccountAdminDialog';
import { AddEntitiesDialog } from './AddEntitiesDialog';
import { PushHistoryTable } from './PushHistoryTable';
import { entityTypeHeading, entityTypeIcon } from './enterprise-entity-labels';
import { GetEnterpriseSubAccounts } from './__generated__/GetEnterpriseSubAccounts';
import { GetSubAccountEntities, GetSubAccountEntitiesVariables } from './__generated__/GetSubAccountEntities';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    backLink: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '13px',
      color: theme.color.grey.neutral200,
      cursor: 'pointer',
      paddingBottom: '8px',
      width: 'fit-content',
    },
    heading: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    containerPanel: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    headingPanel: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    bodyPanel: {
      fontSize: '14px',
      color: '#808080',
    },
    tableHead: {
      '& th': {
        fontWeight: 600,
        fontSize: '11px',
        textTransform: 'uppercase' as const,
        color: theme.color.grey.neutral200,
        borderBottom: '1px solid #e0e0e0',
      },
    },
    typeIcon: {
      fontSize: '16px',
      color: theme.color.grey.neutral200,
      marginRight: '6px',
    },
    entityName: {
      fontWeight: 600,
      fontSize: '14px',
      color: theme.color.grey.neutralBrand900,
    },
    cellText: {
      fontSize: '13px',
      color: theme.color.grey.neutral400,
    },
    statusChip: {
      display: 'inline-block',
      borderRadius: 11,
      padding: '2px 10px',
      fontSize: '12px',
      fontWeight: 600,
    },
    statusActive: {
      backgroundColor: '#e8f5e9',
      color: '#2e7d32',
    },
  })
);

interface MatchParams {
  subTenantId: string;
}

const AdminConsoleSubAccountDetailComponent = (props: RouteComponentProps<MatchParams>) => {
  const { match, history } = props;
  const classes = useStyles();
  const { subTenantId } = match.params;

  const [showInviteDialog, setShowInviteDialog] = React.useState(false);
  const [showAddEntitiesDialog, setShowAddEntitiesDialog] = React.useState(false);

  const { data, loading, refetch } = useCampfireQuery<GetEnterpriseSubAccounts, {}>(GET_ENTERPRISE_SUB_ACCOUNTS);

  const { data: entityData, refetch: refetchEntities } = useCampfireQuery<
    GetSubAccountEntities,
    GetSubAccountEntitiesVariables
  >(GET_SUB_ACCOUNT_ENTITIES, { options: { variables: { subTenantId } } });

  const subAccount = (data?.tm.enterpriseSubAccounts ?? []).find((s) => s.tenantId === subTenantId);
  const subAccountsPath = match.path.replace('/:subTenantId', '');
  const admins = subAccount?.admins ?? [];
  const entities = entityData?.vm.enterpriseSubAccountEntities ?? [];
  const mappedEntities = entities.filter((entity) => entity.isMapped);

  const refetchAll = () => {
    if (refetch) setTimeout(refetch, 0);
    if (refetchEntities) setTimeout(refetchEntities, 0);
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.backLink} onClick={() => history.push(subAccountsPath)}>
        <ChevronLeft style={{ fontSize: '16px' }} /> Sub-Accounts
      </Box>

      {loading && <LinearProgress style={{ marginTop: '1rem' }} />}

      {!loading && !subAccount ? (
        <Box className={classes.containerPanel}>
          <Typography className={classes.headingPanel}>Sub-account not found</Typography>
          <Typography variant='body2' className={classes.bodyPanel}>
            {'This account may have been removed, or it is not one of your sub-accounts.'}
          </Typography>
        </Box>
      ) : (
        subAccount && (
          <>
            <Box className={classes.heading}>
              <Box>
                <Typography variant='h2' className={classes.headingTitle}>
                  {subAccount.name}
                </Typography>
                <Typography variant='body2'>
                  {`${subAccount.fqdn} · Created ${unpackToDateTime(subAccount.dateCreated).toFormat('d MMM y')}`}
                </Typography>
              </Box>
              <ConsoleActionButton
                endIcon={<Add />}
                variant='contained'
                color='primary'
                onClick={() => setShowAddEntitiesDialog(true)}
              >
                Add Entities
              </ConsoleActionButton>
            </Box>

            <AddEntitiesDialog
              open={showAddEntitiesDialog}
              subTenantId={subAccount.tenantId}
              subAccountName={subAccount.name}
              onClose={() => setShowAddEntitiesDialog(false)}
              onAdded={refetchAll}
            />

            <InviteSubAccountAdminDialog
              open={showInviteDialog}
              subTenantId={subAccount.tenantId}
              subAccountName={subAccount.name}
              onClose={() => setShowInviteDialog(false)}
              onInvited={refetchAll}
            />

            <Box className={classes.containerPanel}>
              <Typography className={classes.headingPanel}>
                <Layers style={{ fontSize: '14px', paddingRight: '5px' }} /> Entities
              </Typography>
              <Typography variant='body2' className={classes.bodyPanel}>
                {`${mappedEntities.length} copied from this account and tracked for future updates`}
              </Typography>

              {mappedEntities.length === 0 ? (
                <Box paddingTop='1.5rem'>
                  <Typography variant='body2' className={classes.bodyPanel}>
                    {"Nothing copied across yet. Use 'Add Entities' to choose what this account should have."}
                  </Typography>
                </Box>
              ) : (
                <TableContainer style={{ marginTop: '1rem' }}>
                  <Table>
                    <TableHead className={classes.tableHead}>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align='right'>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mappedEntities.map((entity) => {
                        const TypeIcon = entityTypeIcon(entity.entityType);

                        return (
                          <TableRow key={entity.masterEntityId} hover>
                            <TableCell>
                              <Typography className={classes.entityName}>{entity.name}</Typography>
                            </TableCell>
                            <TableCell>
                              <Box display='flex' alignItems='center'>
                                <TypeIcon className={classes.typeIcon} />
                                <Typography className={classes.cellText}>
                                  {entityTypeHeading(entity.entityType)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align='right'>
                              <span className={`${classes.statusChip} ${classes.statusActive}`}>Copied</span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Box className={classes.containerPanel}>
              <Box display='flex' justifyContent='space-between' alignItems='center' mb='1rem'>
                <Box>
                  <Typography className={classes.headingPanel}>
                    <People style={{ fontSize: '14px', paddingRight: '5px' }} /> Admins
                  </Typography>
                  <Typography variant='body2' className={classes.bodyPanel}>
                    {'People who can administer this sub-account'}
                  </Typography>
                </Box>
                <ConsoleActionButton
                  endIcon={<Add />}
                  variant='contained'
                  color='primary'
                  onClick={() => setShowInviteDialog(true)}
                >
                  Invite Admin
                </ConsoleActionButton>
              </Box>

              {admins.length === 0 ? (
                <Typography variant='body2' className={classes.bodyPanel}>
                  {'No admins yet. Invite someone to give them access to this sub-account.'}
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead className={classes.tableHead}>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Date Added</TableCell>
                        <TableCell align='right'>Access</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {admins.map((admin) => (
                        <TableRow key={admin.userId} hover>
                          <TableCell>
                            <Typography className={classes.entityName}>{admin.displayName}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography className={classes.cellText}>{admin.email}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography className={classes.cellText}>
                              {admin.dateAdded ? unpackToDateTime(admin.dateAdded).toFormat('d MMM y') : ''}
                            </Typography>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography className={classes.cellText}>{admin.access}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
            <Box className={classes.containerPanel}>
              <Typography className={classes.headingPanel}>
                <History style={{ fontSize: '14px', paddingRight: '5px' }} /> Push History
              </Typography>
              <Typography variant='body2' className={classes.bodyPanel}>
                {'Updates sent to this sub-account and how they were resolved'}
              </Typography>
              <PushHistoryTable
                subTenantId={subAccount.tenantId}
                showSubAccount={false}
                emptyMessage='No updates have been pushed to this sub-account yet.'
              />
            </Box>
          </>
        )
      )}
    </Box>
  );
};

export const AdminConsoleSubAccountDetail = withRouter(AdminConsoleSubAccountDetailComponent);
